/**
 * Settings › Hosts at scale: which site a host is on, how bad it is, and the
 * search, filter and By site grouping built on those (owner, 2026-10-21: "once
 * this reaches a significant amount of servers the hosts tab and above NX
 * sites tab will become a nightmare to work through").
 *
 * A host has up to two sites. The CosMon site is the asset's own `site_id`;
 * the NX site is what its agent reports NX calls it. The CosMon site wins, the
 * NX site stands in when there is none, and a host with neither is grouped
 * under "No site" rather than dropped, so a host that needs a site set stays
 * in view.
 *
 * Pure, like `fleet-state.ts`: tested in test/hosts_view.test.ts.
 */

import { loginSummary, nxSites, siteKey, siteState, type NxSite, type SiteHost } from './nx-logins.ts';

export interface ViewHost extends SiteHost {
  kind: string;
  site: { id: string; code: string | null; name: string } | null;
  agentVersion: string | null;
  os: string | null;
  lastReportAt: string | null;
  enrolmentPending: boolean;
  nx: (NonNullable<SiteHost['nx']> & { reachable: boolean; serverName?: string | null; loginPower?: boolean | null }) | null;
}

/** Quiet after five minutes without a report: the rule the State pill has always used. */
const QUIET_MS = 5 * 60_000;

export type Tone = 'ok' | 'warning' | 'stale';

/** Reporting, quiet, or not enrolled — from the last report, not from a stored flag. */
export function hostState(host: ViewHost, now: number): { label: string; tone: Tone; reason: string } {
  if (host.agentId === null) {
    return host.enrolmentPending
      ? { label: 'enrolling', tone: 'warning', reason: 'A token has been issued and no agent has used it yet.' }
      : { label: 'no agent', tone: 'stale', reason: 'Nothing reports for this host yet.' };
  }
  const quiet = host.lastReportAt === null || now - new Date(host.lastReportAt).getTime() > QUIET_MS;
  return quiet
    ? { label: 'quiet', tone: 'warning', reason: 'Enrolled, and nothing has arrived for five minutes.' }
    : { label: 'reporting', tone: 'ok', reason: 'Reports are arriving.' };
}

/** Worst first: a fault before a warning before a host that is fine. */
export type Severity = 'fault' | 'warn' | 'ok';
const RANK: Record<Severity, number> = { fault: 0, warn: 1, ok: 2 };
export const worse = (a: Severity, b: Severity): Severity => (RANK[a] <= RANK[b] ? a : b);

/**
 * How bad one host is. A fault is something that has stopped working: the
 * agent went quiet, NX is not answering, or NX refuses the login. A warning
 * is something not finished or not right yet. A monitoring login with Power
 * User rights is neither: the row says so, and the host stays ok.
 */
export function hostSeverity(host: ViewHost, now: number): Severity {
  const st = hostState(host, now);
  if (st.label === 'quiet') return 'fault';
  if (host.nx !== null && st.label === 'reporting') {
    if (!host.nx.reachable) return 'fault';
    // loginSummary has the rules for whose refusal it is: just after a change it is the old user's, no problem.
    const login = loginSummary(host);
    if (login.problem && (host.nx.login === 'failed' || host.nx.login === 'cert_changed')) return 'fault';
    // A Power User monitoring login is a note on the row, not a warning (owner, 2026-10-21): Add NX
    // site offers one login for both, and a warning on every NX site buries the real ones.
    if (login.problem) return 'warn';
  }
  if (st.label === 'enrolling' || st.label === 'no agent') return 'warn';
  return 'ok';
}

/** Where a host is, as the filter and the By site view see it. */
export interface HostSite {
  /** `site:<uuid>`, an NX `siteKey` (`id:` / `name:`), or `none`. */
  key: string;
  label: string;
  /** Which of the two sites this is. */
  from: 'cosmon' | 'nx' | 'none';
}

export const NO_SITE = 'none';

export function hostSite(host: ViewHost): HostSite {
  if (host.site !== null) {
    const label = host.site.code !== null && host.site.code !== '' && host.site.code !== host.site.name ? `${host.site.code} · ${host.site.name}` : host.site.name;
    return { key: `site:${host.site.id.toLowerCase()}`, label, from: 'cosmon' };
  }
  const nx = siteKey(host);
  if (nx !== null) return { key: nx, label: `${host.nx?.siteName ?? 'NX site'} (NX)`, from: 'nx' };
  return { key: NO_SITE, label: 'No site', from: 'none' };
}

/** Which hosts the chips show. */
export type Show = 'all' | 'attention' | 'nx';
export type Grouping = 'sites' | 'hosts';

export interface HostFilter {
  q: string;
  /** A `hostSite` key, or null for every site. */
  site: string | null;
  show: Show;
  by: Grouping;
}

export const DEFAULT_FILTER: HostFilter = { q: '', site: null, show: 'all', by: 'sites' };

/**
 * The filter as it rides in the URL, after the hash's `?`
 * (`#/settings/hosts?site=…&show=attention`), so a filtered view can be
 * linked and survives a reload. Defaults are never written.
 */
export function parseFilter(query: string): HostFilter {
  const p = new URLSearchParams(query.replace(/^\?/, ''));
  const show = p.get('show');
  return {
    q: p.get('q') ?? '',
    site: p.get('site') || null,
    show: show === 'attention' || show === 'nx' ? show : 'all',
    by: p.get('by') === 'hosts' ? 'hosts' : 'sites',
  };
}

export function formatFilter(f: HostFilter): string {
  const p = new URLSearchParams();
  if (f.q.trim() !== '') p.set('q', f.q);
  if (f.site !== null) p.set('site', f.site);
  if (f.show !== 'all') p.set('show', f.show);
  if (f.by !== 'sites') p.set('by', f.by);
  const s = p.toString();
  return s === '' ? '' : `?${s}`;
}

/** The search box: host code or name, kind, OS, agent version, either site, NX server name. */
export function matches(host: ViewHost, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (needle === '') return true;
  const site = hostSite(host);
  return [
    host.code,
    host.name,
    host.kind,
    host.os,
    host.agentVersion,
    host.agentId,
    site.label,
    host.site?.code,
    host.site?.name,
    host.nx?.siteName,
    host.nx?.serverName,
  ].some((v) => v != null && v.toLowerCase().includes(needle));
}

/** The site picker's options: every site some host is on, by name, with "No site" last. */
export function siteOptions<T extends ViewHost>(hosts: readonly T[]): Array<HostSite & { count: number }> {
  const by = new Map<string, HostSite & { count: number }>();
  for (const h of hosts) {
    const s = hostSite(h);
    const seen = by.get(s.key);
    if (seen === undefined) by.set(s.key, { ...s, count: 1 });
    else seen.count += 1;
  }
  return [...by.values()].sort((a, b) => (a.key === NO_SITE ? 1 : b.key === NO_SITE ? -1 : a.label.localeCompare(b.label)));
}

/** Search and site applied: the scope the chips count over. */
export function scoped<T extends ViewHost>(hosts: readonly T[], f: HostFilter): T[] {
  return hosts.filter((h) => matches(h, f.q) && (f.site === null || hostSite(h).key === f.site));
}

/** How many hosts in scope each chip would show. */
export function chipCounts<T extends ViewHost>(hosts: readonly T[], f: HostFilter, now: number): Record<Show, number> {
  const inScope = scoped(hosts, f);
  return {
    all: inScope.length,
    attention: inScope.filter((h) => hostSeverity(h, now) !== 'ok').length,
    nx: inScope.filter((h) => h.nx !== null).length,
  };
}

/** Everything applied: what the page lists. */
export function visible<T extends ViewHost>(hosts: readonly T[], f: HostFilter, now: number): T[] {
  return scoped(hosts, f).filter((h) =>
    f.show === 'attention' ? hostSeverity(h, now) !== 'ok' : f.show === 'nx' ? h.nx !== null : true,
  );
}

const NX_SEVERITY: Record<'ok' | 'warn' | 'fault', Severity> = { ok: 'ok', warn: 'warn', fault: 'fault' };

export interface SiteGroup<T extends ViewHost> {
  site: HostSite;
  /** Worst first, then by code or name. */
  hosts: T[];
  severity: Severity;
  /** Hosts that are not ok. */
  problems: number;
  /**
   * The NX sites these hosts are on, worst first: each with its login state
   * and its Set up button, in the group's header. Built from EVERY host, not
   * just the visible ones, so setting a site up always covers all its servers.
   */
  nx: Array<NxSite<T>>;
}

const label = (h: ViewHost): string => h.code ?? h.name;

/**
 * The By site view: one group per site, worst first, so a site with a fault
 * is never below a healthy one (the rule Problems by site follows since
 * v2026.10.21). Ties go by name, with "No site" after named sites.
 */
export function siteGroups<T extends ViewHost>(all: readonly T[], shown: readonly T[], now: number): SiteGroup<T>[] {
  const nx = new Map(nxSites([...all]).map((s) => [s.key, s]));
  const by = new Map<string, { site: HostSite; hosts: T[] }>();
  for (const h of shown) {
    const site = hostSite(h);
    const g = by.get(site.key) ?? { site, hosts: [] };
    g.hosts.push(h);
    by.set(site.key, g);
  }
  const groups = [...by.values()].map(({ site, hosts }) => {
    const sev = new Map(hosts.map((h) => [h, hostSeverity(h, now)]));
    hosts.sort((a, b) => RANK[sev.get(a)!] - RANK[sev.get(b)!] || label(a).localeCompare(label(b)));
    const keys = new Set(hosts.map((h) => siteKey(h)).filter((k): k is string => k !== null));
    const sites = [...keys]
      .map((k) => nx.get(k))
      .filter((s): s is NxSite<T> => s !== undefined)
      .sort((a, b) => RANK[NX_SEVERITY[siteState(a).tone]] - RANK[NX_SEVERITY[siteState(b).tone]] || a.name.localeCompare(b.name));
    let severity: Severity = 'ok';
    for (const s of sev.values()) severity = worse(severity, s);
    for (const s of sites) severity = worse(severity, NX_SEVERITY[siteState(s).tone]);
    return { site, hosts, severity, problems: [...sev.values()].filter((s) => s !== 'ok').length, nx: sites };
  });
  return groups.sort(
    (a, b) =>
      RANK[a.severity] - RANK[b.severity] ||
      b.problems - a.problems ||
      (a.site.key === NO_SITE ? 1 : b.site.key === NO_SITE ? -1 : a.site.label.localeCompare(b.site.label)),
  );
}
